// The states and provinces a Studio Map shows, keyed by ISO 3166-2 code (the
// name of each outline in usCanada.json), and how a dimension's terms are
// matched to them: by code or name, however they're cased, accented or
// punctuated. Centroids are Natural Earth's label points, so a bubble lands
// inside its state (Florida's peninsula, Michigan's lower half).
export interface Region {
  // 'US-TX', 'CA-ON'
  id: string;
  // 'TX', 'ON'
  code: string;
  name: string;
  country: 'US' | 'CA';
  // [longitude, latitude]
  centroid: [number, number];
}

const ROWS: [string, string, [number, number]][] = [
  ['CA-AB', 'Alberta', [-115, 55.28]],
  ['CA-BC', 'British Columbia', [-124.66, 54.69]],
  ['CA-MB', 'Manitoba', [-97.38, 54.85]],
  ['CA-NB', 'New Brunswick', [-66.46, 46.58]],
  ['CA-NL', 'Newfoundland and Labrador', [-56.22, 48.66]],
  ['CA-NS', 'Nova Scotia', [-62.81, 45.23]],
  ['CA-NT', 'Northwest Territories', [-119.94, 64.08]],
  ['CA-NU', 'Nunavut', [-97.14, 64.39]],
  ['CA-ON', 'Ontario', [-84.79, 50.52]],
  ['CA-PE', 'Prince Edward Island', [-63.39, 46.34]],
  ['CA-QC', 'Quebec', [-73.72, 52.26]],
  ['CA-SK', 'Saskatchewan', [-105.68, 54.5]],
  ['CA-YT', 'Yukon', [-135.7, 63.61]],
  ['US-AK', 'Alaska', [-151.6, 65.36]],
  ['US-AL', 'Alabama', [-86.72, 32.86]],
  ['US-AR', 'Arkansas', [-92.14, 34.76]],
  ['US-AZ', 'Arizona', [-111.94, 34.3]],
  ['US-CA', 'California', [-119.59, 36.75]],
  ['US-CO', 'Colorado', [-105.54, 39.0]],
  ['US-CT', 'Connecticut', [-72.76, 41.65]],
  ['US-DC', 'District of Columbia', [-77.01, 38.89]],
  ['US-DE', 'Delaware', [-75.41, 38.87]],
  ['US-FL', 'Florida', [-81.62, 28.16]],
  ['US-GA', 'Georgia', [-83.41, 32.85]],
  ['US-HI', 'Hawaii', [-158.0, 21.49]],
  ['US-IA', 'Iowa', [-93.39, 42.04]],
  ['US-ID', 'Idaho', [-114.13, 43.78]],
  ['US-IL', 'Illinois', [-89.2, 39.95]],
  ['US-IN', 'Indiana', [-86.14, 39.89]],
  ['US-KS', 'Kansas', [-98.33, 38.5]],
  ['US-KY', 'Kentucky', [-85.57, 37.4]],
  ['US-LA', 'Louisiana', [-92.0, 30.53]],
  ['US-MA', 'Massachusetts', [-72.0, 42.37]],
  ['US-MD', 'Maryland', [-77.05, 39.39]],
  ['US-ME', 'Maine', [-69.2, 45.15]],
  ['US-MI', 'Michigan', [-84.95, 43.43]],
  ['US-MN', 'Minnesota', [-93.36, 46.06]],
  ['US-MO', 'Missouri', [-92.45, 38.55]],
  ['US-MS', 'Mississippi', [-89.72, 32.87]],
  ['US-MT', 'Montana', [-110.04, 47.0]],
  ['US-NC', 'North Carolina', [-78.87, 35.62]],
  ['US-ND', 'North Dakota', [-100.3, 47.47]],
  ['US-NE', 'Nebraska', [-99.69, 41.5]],
  ['US-NH', 'New Hampshire', [-71.63, 43.6]],
  ['US-NJ', 'New Jersey', [-74.47, 40.04]],
  ['US-NM', 'New Mexico', [-106.02, 34.5]],
  ['US-NV', 'Nevada', [-117.02, 39.43]],
  ['US-NY', 'New York', [-75.32, 43.2]],
  ['US-OH', 'Ohio', [-82.67, 40.09]],
  ['US-OK', 'Oklahoma', [-97.13, 35.45]],
  ['US-OR', 'Oregon', [-120.39, 43.83]],
  ['US-PA', 'Pennsylvania', [-77.61, 40.86]],
  ['US-RI', 'Rhode Island', [-71.51, 41.62]],
  ['US-SC', 'South Carolina', [-80.65, 33.86]],
  ['US-SD', 'South Dakota', [-100.25, 44.47]],
  ['US-TN', 'Tennessee', [-86.34, 35.75]],
  ['US-TX', 'Texas', [-98.76, 31.13]],
  ['US-UT', 'Utah', [-111.54, 39.5]],
  ['US-VA', 'Virginia', [-78.24, 37.74]],
  ['US-VT', 'Vermont', [-72.73, 44.09]],
  ['US-WA', 'Washington', [-120.36, 47.49]],
  ['US-WI', 'Wisconsin', [-89.58, 44.37]],
  ['US-WV', 'West Virginia', [-80.71, 38.64]],
  ['US-WY', 'Wyoming', [-107.55, 43.0]],
];

export const REGIONS: Region[] = ROWS.map(([id, name, centroid]) => ({
  id,
  code: id.slice(3),
  name,
  country: id.startsWith('US') ? 'US' : 'CA',
  centroid,
}));

const REGION_BY_ID = new Map(REGIONS.map((region) => [region.id, region]));

// Older and other spellings still found in TMS data
const ALIASES: Record<string, string> = {
  PQ: 'CA-QC',
  NF: 'CA-NL',
  LB: 'CA-NL',
  YK: 'CA-YT',
  NEWFOUNDLAND: 'CA-NL',
  LABRADOR: 'CA-NL',
  'YUKON TERRITORY': 'CA-YT',
  'WASHINGTON DC': 'US-DC',
};

// Upper case, without accents, punctuation or extra spaces: 'Québec' and
// 'QUEBEC', 'D.C.' and 'DC' match
const normalise = (term: string) =>
  term
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z ]/g, (char) => (char === '.' ? '' : ' '))
    .replace(/\s+/g, ' ')
    .trim();

const REGION_ID_BY_TERM = new Map<string, string>([
  ...REGIONS.flatMap((region): [string, string][] => [
    [region.code, region.id],
    [normalise(region.name), region.id],
    // As an ISO code: 'US-TX'
    [normalise(region.id), region.id],
  ]),
  ...Object.entries(ALIASES),
]);

// The state or province a term stands for, if any: as it is, or without a
// trailing country ('Ontario, Canada', 'TX USA')
export const toRegionId = (term: string): string | undefined => {
  const normalised = normalise(term);
  return (
    REGION_ID_BY_TERM.get(normalised) ??
    REGION_ID_BY_TERM.get(
      normalised.replace(/ (USA|US|UNITED STATES|CANADA|CAN)$/, ''),
    )
  );
};

export const getRegion = (id: string): Region | undefined =>
  REGION_BY_ID.get(id);

// The regions each of a Map's areas shows. The US and Canada together
// leave Hawaii out: it would sit far off the rest.
export const getAreaRegionIds = (
  area: NonNullable<Studio.MapOptions['region']>,
): string[] =>
  REGIONS.filter((region) => {
    if (area === 'us') {
      return region.country === 'US';
    }
    if (area === 'canada') {
      return region.country === 'CA';
    }
    return region.id !== 'US-HI';
  }).map((region) => region.id);
